import React, { useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, Calendar, Briefcase, Wallet, Search, FileText, Users, Globe, BarChart3, MessageSquare, ArrowRight, UserPlus, Star, Bell, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Accordion } from '../components/ui/Accordion';
interface ForAstrologersPageProps {
  onNavigate: (page: string) => void;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ForAstrologersPage({
  onNavigate
}: ForAstrologersPageProps) {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [clientsPerWeek, setClientsPerWeek] = useState(7);
  const [avgCheck, setAvgCheck] = useState(4500);
  const monthlyIncome = clientsPerWeek * avgCheck * 4;
  const formRef = useRef<HTMLDivElement>(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      onNavigate('login');
    }, 2000);
  };
  const faqItems = [{
    id: '1',
    title: 'Откуда приходят клиенты?',
    content: 'Клиенты находят вас через общий каталог специалистов, внутреннюю систему рекомендаций платформы, а также благодаря нашему маркетинговому продвижению сервиса.'
  }, {
    id: '2',
    title: 'Как происходит оплата?',
    content: 'Оплата зачисляется на ваш внутренний баланс сразу после завершения консультации или покупки отчёта. Вывод средств доступен на вашу карту.'
  }, {
    id: '3',
    title: 'Есть ли комиссия?',
    content: 'Да, платформа удерживает комиссию за привлечение клиентов и предоставление IT-инфраструктуры. Процент комиссии зависит от выбранного вами тарифа.'
  }, {
    id: '4',
    title: 'Сколько времени занимает модерация?',
    content: 'Обычно проверка новых профилей занимает не более 24 часов. Мы проверяем корректность заполнения данных и ваш опыт.'
  }, {
    id: '5',
    title: 'Нужен ли опыт?',
    content: 'Мы работаем как с опытными астрологами, так и с начинающими специалистами. Главный критерий — качество ваших консультаций и удовлетворенность клиентов.'
  }];
  return <div className="min-h-screen bg-[#050b14] text-slate-300 font-sans selection:bg-indigo-500/30 pb-20 sm:pb-0" data-id="element-23748">
      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#050b14]/90 backdrop-blur-md border-t border-white/10 z-50 sm:hidden" data-id="element-23749">
        <Button fullWidth className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 !text-slate-900 font-bold shadow-[0_0_20px_rgba(245,158,11,0.3)] border-none" onClick={scrollToForm} data-id="element-23750">
          Начать работу
        </Button>
      </div>

      {/* 1. HERO SECTION */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32 overflow-hidden" data-id="element-23751">
        {/* Rich Background Gradients */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/20 via-[#050b14] to-[#050b14]" data-id="element-23752"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[120px]" data-id="element-23753"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px]" data-id="element-23754"></div>

        {/* Floating decorative elements */}
        <motion.div animate={{
        y: [0, -18, 0],
        rotate: [0, 5, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-28 left-8 text-amber-500/15 hidden lg:block pointer-events-none" data-id="element-23755">
          <Star className="w-14 h-14" data-id="element-23756" />
        </motion.div>
        <motion.div animate={{
        y: [0, 15, 0],
        rotate: [0, -8, 0]
      }} transition={{
        duration: 7,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} className="absolute bottom-24 right-12 text-indigo-500/15 hidden lg:block pointer-events-none" data-id="element-23757">
          <Sparkles className="w-10 h-10" data-id="element-23758" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23759">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-23760">
            {/* Left: Content */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} className="text-center lg:text-left" data-id="element-23761">
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-6 px-4 py-1.5 text-sm backdrop-blur-sm" data-id="element-23762">
                <Sparkles className="w-4 h-4 mr-2" data-id="element-23763" /> Для профессионалов
              </Badge>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 sm:mb-6 leading-tight tracking-tight" data-id="element-23764">
                Платформа для астрологов с{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400" data-id="element-23765">
                  готовым потоком клиентов
                </span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0 font-light" data-id="element-23766">
                Создавайте отчёты, проводите консультации и зарабатывайте в
                одном кабинете
              </p>

              <ul className="space-y-3 mb-6 sm:mb-10 text-left max-w-md mx-auto lg:mx-0" data-id="element-23767">
                {['Клиенты приходят через платформу', 'Автоматическая генерация отчётов', 'Удобный личный кабинет'].map((item, i) => <li key={i} className="flex items-center text-slate-200" data-id="element-23768">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-emerald-500/20 flex items-center justify-center mr-2.5 sm:mr-3 flex-shrink-0" data-id="element-23769">
                      <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400" data-id="element-23770" />
                    </div>
                    <span className="text-sm sm:text-base font-medium" data-id="element-23771">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-23772">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 !text-slate-900 font-bold shadow-[0_0_30px_rgba(245,158,11,0.3)] border-none" onClick={scrollToForm} data-id="element-23773">
                  Начать работу
                </Button>
                <Button size="lg" variant="outline" className="!border-white/40 !text-white hover:!bg-white/10 !bg-white/5 backdrop-blur-sm" onClick={() => {
                document.getElementById('how-it-works')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} data-id="element-23774">
                  Посмотреть возможности
                </Button>
              </div>
              <p className="mt-5 text-sm text-slate-400 text-center lg:text-left font-medium" data-id="element-23775">
                Модерация профиля до 24 часов
              </p>
            </motion.div>

            {/* Right: Visual */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative hidden sm:block" data-id="element-23776">
              {/* Glow behind mock */}
              <div className="absolute inset-0 bg-indigo-500/20 blur-3xl rounded-full" data-id="element-23777"></div>

              <div className="relative rounded-2xl border border-white/10 bg-[#0a1628]/80 backdrop-blur-xl p-6 shadow-2xl" data-id="element-23778">
                {/* Mock UI Header */}
                <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4" data-id="element-23779">
                  <div className="flex items-center gap-3" data-id="element-23780">
                    <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center" data-id="element-23781">
                      <Wallet className="w-5 h-5 text-indigo-400" data-id="element-23782" />
                    </div>
                    <div data-id="element-23783">
                      <div className="text-sm text-slate-400" data-id="element-23784">Баланс</div>
                      <div className="text-xl font-bold text-white" data-id="element-23785">
                        84 500 ₽
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mock UI Content */}
                <div className="space-y-4" data-id="element-23786">
                  {[{
                  name: 'Мария В.',
                  service: 'Натальная карта',
                  price: '+ 4 000 ₽',
                  time: 'Сегодня, 14:30'
                }, {
                  name: 'Елена С.',
                  service: 'Консультация',
                  price: '+ 5 000 ₽',
                  time: 'Вчера, 18:00'
                }, {
                  name: 'Алексей Д.',
                  service: 'Совместимость',
                  price: '+ 3 500 ₽',
                  time: 'Вчера, 12:15'
                }].map((item, i) => <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-23787">
                      <div className="flex items-center gap-3" data-id="element-23788">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white shadow-inner" data-id="element-23789">
                          {item.name.charAt(0)}
                        </div>
                        <div data-id="element-23790">
                          <div className="text-sm font-medium text-white" data-id="element-23791">
                            {item.name}
                          </div>
                          <div className="text-xs text-slate-400" data-id="element-23792">
                            {item.service}
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-23793">
                        <div className="text-sm font-bold text-emerald-400" data-id="element-23794">
                          {item.price}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-23795">
                          {item.time}
                        </div>
                      </div>
                    </div>)}
                </div>

                {/* Static Micro Notification */}
                <div className="absolute -right-6 top-1/3 bg-[#1e293b] p-4 rounded-xl shadow-2xl border border-white/10 z-20 flex items-center gap-3 transform hover:scale-105 transition-transform" data-id="element-23796">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-23797">
                    <Bell className="w-4 h-4 text-amber-400" data-id="element-23798" />
                  </div>
                  <div data-id="element-23799">
                    <div className="text-xs text-slate-400 font-medium" data-id="element-23800">
                      Новая заявка
                    </div>
                    <div className="text-sm font-bold text-white" data-id="element-23801">
                      Консультация 5 000 ₽
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. КАК ЭТО РАБОТАЕТ */}
      <section id="how-it-works" className="py-16 sm:py-24 bg-[#0a1628] border-y border-white/5 relative overflow-hidden" data-id="element-23802">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23803">
          <div className="text-center mb-12 sm:mb-16" data-id="element-23804">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-23805">
              Как устроена работа
            </h2>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto" data-id="element-23806">
              Все процессы проходят внутри платформы
            </p>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 relative" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} data-id="element-23807">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent -translate-y-1/2 z-0" data-id="element-23808"></div>

            {[{
            step: '01',
            title: 'Вы создаёте профиль',
            desc: 'Заполняете информацию о себе, опыте и услугах.',
            icon: <UserPlus className="w-6 h-6 text-indigo-400" data-id="element-23809" />
          }, {
            step: '02',
            title: 'Получаете заявки от клиентов',
            desc: 'Клиенты находят вас в каталоге и записываются на услуги.',
            icon: <Search className="w-6 h-6 text-amber-400" data-id="element-23810" />
          }, {
            step: '03',
            title: 'Проводите консультации',
            desc: 'Оказываете услуги и получаете оплату на баланс.',
            icon: <Briefcase className="w-6 h-6 text-emerald-400" data-id="element-23811" />
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative z-10" data-id="element-23812">
                <div className="bg-[#050b14] rounded-2xl p-5 sm:p-8 border border-white/5 h-full relative hover:border-white/15 transition-colors group shadow-lg" data-id="element-23813">
                  <div className="text-3xl sm:text-5xl font-black text-white/5 absolute top-3 sm:top-4 right-4 sm:right-6 group-hover:text-white/10 transition-colors" data-id="element-23814">
                    {item.step}
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform" data-id="element-23815">
                    {item.icon}
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3" data-id="element-23816">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed" data-id="element-23817">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 3. КАЛЬКУЛЯТОР ДОХОДА */}
      <section className="py-16 sm:py-24 relative overflow-hidden" data-id="element-23818">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-indigo-900/20 rounded-full blur-[120px] pointer-events-none" data-id="element-23819"></div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23820">
          <div className="text-center mb-10" data-id="element-23821">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3" data-id="element-23822">
              Рассчитайте свой доход
            </h2>
            <p className="text-slate-400" data-id="element-23823">
              Двигайте ползунки — результат обновляется мгновенно
            </p>
          </div>

          <div className="bg-[#0a1628]/80 backdrop-blur-sm rounded-2xl border border-white/10 p-6 sm:p-8" data-id="element-23824">
            {/* Slider 1: Clients per week */}
            <div className="mb-8" data-id="element-23825">
              <div className="flex justify-between items-center mb-3" data-id="element-23826">
                <label className="text-sm text-slate-300 font-medium" data-id="element-23827">
                  Клиентов в неделю
                </label>
                <span className="text-lg font-bold text-white bg-white/5 px-3 py-1 rounded-lg tabular-nums" data-id="element-23828">
                  {clientsPerWeek}
                </span>
              </div>
              <input type="range" min={1} max={20} value={clientsPerWeek} onChange={e => setClientsPerWeek(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer bg-white/10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500 [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(99,102,241,0.5)] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-indigo-500 [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer" style={{
              background: `linear-gradient(to right, #6366f1 ${(clientsPerWeek - 1) / 19 * 100}%, rgba(255,255,255,0.1) ${(clientsPerWeek - 1) / 19 * 100}%)`
            }} data-id="element-23829" />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1.5" data-id="element-23830">
                <span data-id="element-23831">1</span>
                <span data-id="element-23832">10</span>
                <span data-id="element-23833">20</span>
              </div>
            </div>

            {/* Slider 2: Average check */}
            <div className="mb-8" data-id="element-23834">
              <div className="flex justify-between items-center mb-3" data-id="element-23835">
                <label className="text-sm text-slate-300 font-medium" data-id="element-23836">
                  Средний чек
                </label>
                <span className="text-lg font-bold text-white bg-white/5 px-3 py-1 rounded-lg tabular-nums" data-id="element-23837">
                  {avgCheck.toLocaleString('ru-RU')} ₽
                </span>
              </div>
              <input type="range" min={1000} max={15000} step={500} value={avgCheck} onChange={e => setAvgCheck(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer bg-white/10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-500 [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(245,158,11,0.5)] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-amber-500 [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer" style={{
              background: `linear-gradient(to right, #f59e0b ${(avgCheck - 1000) / 14000 * 100}%, rgba(255,255,255,0.1) ${(avgCheck - 1000) / 14000 * 100}%)`
            }} data-id="element-23838" />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1.5" data-id="element-23839">
                <span data-id="element-23840">1 000 ₽</span>
                <span data-id="element-23841">8 000 ₽</span>
                <span data-id="element-23842">15 000 ₽</span>
              </div>
            </div>

            {/* Result */}
            <div className="relative overflow-hidden rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-amber-500/10 p-6 text-center" data-id="element-23843">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-500/5 via-transparent to-transparent" data-id="element-23844"></div>
              <div className="relative z-10" data-id="element-23845">
                <div className="text-sm text-slate-400 mb-2 font-medium" data-id="element-23846">
                  Ориентировочный доход в месяц
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-400 to-amber-400 tabular-nums" data-id="element-23847">
                  ≈ {monthlyIncome.toLocaleString('ru-RU')} ₽
                </div>
                <div className="text-xs text-slate-500 mt-3" data-id="element-23848">
                  {clientsPerWeek} клиентов × {avgCheck.toLocaleString('ru-RU')}{' '}
                  ₽ × 4 недели
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-slate-500 mt-4" data-id="element-23849">
              Доход зависит от активности и рейтинга профиля
            </p>
          </div>
        </div>
      </section>

      {/* 4. ОТКУДА КЛИЕНТЫ */}
      <section className="py-16 sm:py-24 bg-[#0a1628] border-y border-white/5 relative" data-id="element-23850">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23851">
          <div className="text-center mb-12" data-id="element-23852">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-23853">
              Как вы получаете клиентов
            </h2>
            <p className="text-indigo-400 font-medium text-sm sm:text-base" data-id="element-23854">
              Мы приводим клиентов, вы работаете с ними
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-23855">
            {[{
            text: 'Каталог специалистов',
            icon: <Search className="w-4 h-4 text-indigo-400" data-id="element-23856" />,
            desc: 'Клиенты находят вас по специализации и рейтингу'
          }, {
            text: 'Система рекомендаций',
            icon: <Star className="w-4 h-4 text-amber-400" data-id="element-23857" />,
            desc: 'Алгоритм подбирает вам подходящих клиентов'
          }, {
            text: 'Продвижение платформы',
            icon: <Globe className="w-4 h-4 text-emerald-400" data-id="element-23858" />,
            desc: 'SEO, реклама и контент-маркетинг за наш счёт'
          }, {
            text: 'Повторные обращения',
            icon: <Users className="w-4 h-4 text-violet-400" data-id="element-23859" />,
            desc: 'Клиенты возвращаются через личный кабинет'
          }].map((item, i) => <div key={i} className="bg-[#050b14] p-4 rounded-xl border border-white/5 hover:border-indigo-500/20 transition-colors group" data-id="element-23860">
                <div className="flex items-center gap-3 mb-2" data-id="element-23861">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-white/10 transition-colors" data-id="element-23862">
                    {item.icon}
                  </div>
                  <span className="text-sm font-semibold text-white" data-id="element-23863">
                    {item.text}
                  </span>
                </div>
                <p className="text-xs text-slate-500 ml-11" data-id="element-23864">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. ВОЗМОЖНОСТИ ПЛАТФОРМЫ */}
      <section className="py-14 sm:py-20 relative overflow-hidden" data-id="element-23865">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23866">
          <div className="text-center mb-10" data-id="element-23867">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-23868">
              Всё для работы — в одном кабинете
            </h2>
            <p className="text-sm text-slate-400 max-w-lg mx-auto" data-id="element-23869">
              Не нужно переключаться между сервисами — всё уже встроено
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-23870">
            {/* Left: Feature cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-23871">
              {[{
              icon: <FileText className="w-5 h-5 text-indigo-400" data-id="element-23872" />,
              title: 'AI-отчёты',
              desc: 'Натальные карты, соляры и прогнозы генерируются автоматически',
              color: 'from-indigo-500/10 to-indigo-500/5'
            }, {
              icon: <Calendar className="w-5 h-5 text-emerald-400" data-id="element-23873" />,
              title: 'Календарь',
              desc: 'Расписание консультаций с напоминаниями для вас и клиентов',
              color: 'from-emerald-500/10 to-emerald-500/5'
            }, {
              icon: <MessageSquare className="w-5 h-5 text-amber-400" data-id="element-23874" />,
              title: 'Чат и видео',
              desc: 'Встроенный мессенджер и видеозвонки без сторонних сервисов',
              color: 'from-amber-500/10 to-amber-500/5'
            }, {
              icon: <BarChart3 className="w-5 h-5 text-violet-400" data-id="element-23875" />,
              title: 'Аналитика',
              desc: 'Доход, конверсии, рейтинг — всё в реальном времени',
              color: 'from-violet-500/10 to-violet-500/5'
            }].map((item, i) => <div key={i} className={`bg-gradient-to-br ${item.color} rounded-xl border border-white/5 p-4 hover:border-white/15 transition-colors`} data-id="element-23876">
                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center mb-3" data-id="element-23877">
                    {item.icon}
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1" data-id="element-23878">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-400 leading-relaxed" data-id="element-23879">
                    {item.desc}
                  </p>
                </div>)}
            </div>

            {/* Right: Mock dashboard with real content */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-23880">
              <div className="absolute -inset-3 bg-indigo-500/10 rounded-2xl blur-2xl" data-id="element-23881"></div>
              <div className="relative bg-[#0a1628] border border-white/10 rounded-xl overflow-hidden shadow-2xl" data-id="element-23882">
                {/* Browser chrome */}
                <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#050b14] border-b border-white/5" data-id="element-23883">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" data-id="element-23884"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" data-id="element-23885"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" data-id="element-23886"></div>
                  <div className="ml-3 flex-1 h-5 bg-white/5 rounded-md flex items-center px-2" data-id="element-23887">
                    <span className="text-[9px] text-slate-500" data-id="element-23888">
                      astriqo.com/dashboard
                    </span>
                  </div>
                </div>
                {/* Dashboard content */}
                <div className="p-4" data-id="element-23889">
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2 mb-3" data-id="element-23890">
                    {[{
                    label: 'Доход',
                    value: '84 500 ₽',
                    change: '+12%',
                    color: 'text-emerald-400'
                  }, {
                    label: 'Клиенты',
                    value: '23',
                    change: '+5',
                    color: 'text-indigo-400'
                  }, {
                    label: 'Рейтинг',
                    value: '4.9',
                    change: '★',
                    color: 'text-amber-400'
                  }].map((s, i) => <div key={i} className="bg-white/5 rounded-lg p-2.5" data-id="element-23891">
                        <div className="text-[9px] text-slate-500 mb-0.5" data-id="element-23892">
                          {s.label}
                        </div>
                        <div className="text-sm font-bold text-white" data-id="element-23893">
                          {s.value}
                        </div>
                        <div className={`text-[9px] font-medium ${s.color}`} data-id="element-23894">
                          {s.change}
                        </div>
                      </div>)}
                  </div>
                  {/* Mini chart */}
                  <div className="bg-white/5 rounded-lg p-3 mb-3" data-id="element-23895">
                    <div className="text-[9px] text-slate-500 mb-2" data-id="element-23896">
                      Доход за неделю
                    </div>
                    <div className="flex items-end gap-1 h-12" data-id="element-23897">
                      {[40, 65, 45, 80, 60, 90, 75].map((h, i) => <div key={i} className="flex-1 bg-gradient-to-t from-indigo-500/40 to-indigo-500/80 rounded-sm" style={{
                      height: `${h}%`
                    }} data-id="element-23898"></div>)}
                    </div>
                    <div className="flex justify-between mt-1" data-id="element-23899">
                      {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(d => <span key={d} className="text-[7px] text-slate-600 flex-1 text-center" data-id="element-23900">
                          {d}
                        </span>)}
                    </div>
                  </div>
                  {/* Recent orders */}
                  <div className="space-y-1.5" data-id="element-23901">
                    {[{
                    name: 'Мария В.',
                    type: 'Натальная карта',
                    amount: '+4 000 ₽'
                  }, {
                    name: 'Елена С.',
                    type: 'Консультация',
                    amount: '+5 000 ₽'
                  }].map((o, i) => <div key={i} className="flex items-center justify-between bg-white/[0.03] rounded-lg px-3 py-2" data-id="element-23902">
                        <div className="flex items-center gap-2" data-id="element-23903">
                          <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-[8px] font-bold text-indigo-300" data-id="element-23904">
                            {o.name[0]}
                          </div>
                          <div data-id="element-23905">
                            <div className="text-[10px] font-medium text-white" data-id="element-23906">
                              {o.name}
                            </div>
                            <div className="text-[8px] text-slate-500" data-id="element-23907">
                              {o.type}
                            </div>
                          </div>
                        </div>
                        <span className="text-[10px] font-bold text-emerald-400" data-id="element-23908">
                          {o.amount}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. КАК НАЧАТЬ */}
      <section className="py-12 sm:py-16 relative" data-id="element-23909">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23910">
          <div className="text-center mb-8" data-id="element-23911">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2" data-id="element-23912">
              Старт за 1 день
            </h2>
            <p className="text-sm text-slate-400" data-id="element-23913">
              От регистрации до первого клиента — 5 простых шагов
            </p>
          </div>

          {/* Horizontal timeline on desktop, compact vertical on mobile */}
          <div className="hidden sm:flex items-start justify-between relative mb-10" data-id="element-23914">
            {/* Connecting line */}
            <div className="absolute top-5 left-[10%] right-[10%] h-px bg-gradient-to-r from-indigo-500/30 via-amber-500/30 to-emerald-500/30" data-id="element-23915"></div>

            {[{
            num: '1',
            label: 'Регистрация',
            desc: '2 минуты',
            color: 'text-indigo-400 bg-indigo-500/15 border-indigo-500/30'
          }, {
            num: '2',
            label: 'Профиль',
            desc: '15 минут',
            color: 'text-indigo-400 bg-indigo-500/15 border-indigo-500/30'
          }, {
            num: '3',
            label: 'Модерация',
            desc: 'до 24ч',
            color: 'text-amber-400 bg-amber-500/15 border-amber-500/30'
          }, {
            num: '4',
            label: 'Заявки',
            desc: 'автоматически',
            color: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30'
          }, {
            num: '5',
            label: 'Работа',
            desc: 'зарабатываете',
            color: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30'
          }].map((step, i) => <div key={i} className="flex flex-col items-center relative z-10 flex-1" data-id="element-23916">
                <div className={`w-10 h-10 rounded-full ${step.color} border flex items-center justify-center font-bold text-sm mb-2`} data-id="element-23917">
                  {step.num}
                </div>
                <span className="text-xs font-semibold text-white" data-id="element-23918">
                  {step.label}
                </span>
                <span className="text-[10px] text-slate-500" data-id="element-23919">{step.desc}</span>
              </div>)}
          </div>

          {/* Mobile: compact list */}
          <div className="sm:hidden space-y-2 mb-8" data-id="element-23920">
            {[{
            num: '1',
            label: 'Регистрация',
            desc: '2 мин'
          }, {
            num: '2',
            label: 'Заполнение профиля',
            desc: '15 мин'
          }, {
            num: '3',
            label: 'Модерация',
            desc: 'до 24ч'
          }, {
            num: '4',
            label: 'Получение заявок',
            desc: 'авто'
          }, {
            num: '5',
            label: 'Работа с клиентами',
            desc: '💰'
          }].map((step, i) => <div key={i} className="flex items-center gap-3 bg-[#0a1628] px-4 py-3 rounded-xl border border-white/5" data-id="element-23921">
                <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-bold text-xs flex-shrink-0" data-id="element-23922">
                  {step.num}
                </div>
                <span className="text-sm font-medium text-white flex-1" data-id="element-23923">
                  {step.label}
                </span>
                <span className="text-xs text-slate-500" data-id="element-23924">{step.desc}</span>
              </div>)}
          </div>

          <div className="text-center" data-id="element-23925">
            <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 !text-slate-900 font-bold shadow-[0_0_30px_rgba(245,158,11,0.3)] border-none" onClick={scrollToForm} data-id="element-23926">
              Начать работу
            </Button>
          </div>
        </div>
      </section>

      {/* 8. ТАРИФЫ */}
      <section className="py-16 sm:py-24 bg-[#0a1628] border-y border-white/5 relative overflow-hidden" data-id="element-23927">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-indigo-900/10 rounded-full blur-[120px] pointer-events-none" data-id="element-23928"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23929">
          <div className="text-center mb-12 sm:mb-16" data-id="element-23930">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3" data-id="element-23931">
              Выберите свой тариф
            </h2>
            <p className="text-sm text-slate-400" data-id="element-23932">
              Начните бесплатно — масштабируйтесь по мере роста
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 items-stretch" data-id="element-23933">
            {/* БАЗОВЫЙ */}
            <div className="bg-[#050b14] rounded-2xl border border-white/10 p-6 flex flex-col hover:border-white/20 transition-all group" data-id="element-23934">
              <div className="mb-5" data-id="element-23935">
                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center mb-4" data-id="element-23936">
                  <Star className="w-5 h-5 text-slate-400" data-id="element-23937" />
                </div>
                <h3 className="text-lg font-bold text-white" data-id="element-23938">Базовый</h3>
                <div className="mt-2" data-id="element-23939">
                  <span className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-23940">
                    0 ₽
                  </span>
                  <span className="text-xs sm:text-sm text-slate-500 ml-1" data-id="element-23941">
                    / мес
                  </span>
                </div>
              </div>

              <div className="h-px bg-white/5 mb-5" data-id="element-23942"></div>

              <ul className="space-y-3 mb-8 flex-grow text-sm" data-id="element-23943">
                <li className="flex items-center gap-2.5 text-slate-400" data-id="element-23944">
                  <div className="w-5 h-5 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-23945">
                    <Calendar className="w-3 h-3 text-slate-500" data-id="element-23946" />
                  </div>
                  <span data-id="element-23947">5 консультаций / мес</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-400" data-id="element-23948">
                  <div className="w-5 h-5 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-23949">
                    <Wallet className="w-3 h-3 text-slate-500" data-id="element-23950" />
                  </div>
                  <span data-id="element-23951">
                    Комиссия{' '}
                    <span className="text-white font-semibold" data-id="element-23952">25%</span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-400" data-id="element-23953">
                  <div className="w-5 h-5 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-23954">
                    <Search className="w-3 h-3 text-slate-500" data-id="element-23955" />
                  </div>
                  <span data-id="element-23956">Без приоритета в поиске</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-400" data-id="element-23957">
                  <div className="w-5 h-5 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-23958">
                    <Briefcase className="w-3 h-3 text-slate-500" data-id="element-23959" />
                  </div>
                  <span data-id="element-23960">До 3 услуг</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-400" data-id="element-23961">
                  <div className="w-5 h-5 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-23962">
                    <BarChart3 className="w-3 h-3 text-slate-500" data-id="element-23963" />
                  </div>
                  <span data-id="element-23964">Базовая аналитика</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-400" data-id="element-23965">
                  <div className="w-5 h-5 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-23966">
                    <MessageSquare className="w-3 h-3 text-slate-500" data-id="element-23967" />
                  </div>
                  <span data-id="element-23968">Поддержка по email</span>
                </li>
              </ul>
              <Button fullWidth variant="outline" className="!border-white/15 !text-slate-300 hover:!bg-white/5 !bg-transparent" onClick={scrollToForm} data-id="element-23969">
                Начать бесплатно
              </Button>
            </div>

            {/* PRO — рекомендуемый */}
            <div className="bg-gradient-to-b from-indigo-950/80 to-[#050b14] rounded-2xl border-2 border-indigo-500/50 p-6 flex flex-col relative shadow-[0_0_40px_rgba(99,102,241,0.12)] md:-translate-y-3" data-id="element-23970">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2" data-id="element-23971">
                <span className="bg-indigo-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg shadow-indigo-500/30" data-id="element-23972">
                  Популярный
                </span>
              </div>

              <div className="mb-5 mt-1" data-id="element-23973">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-4" data-id="element-23974">
                  <Sparkles className="w-5 h-5 text-indigo-400" data-id="element-23975" />
                </div>
                <h3 className="text-lg font-bold text-indigo-300" data-id="element-23976">PRO</h3>
                <div className="mt-2" data-id="element-23977">
                  <span className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-23978">
                    990 ₽
                  </span>
                  <span className="text-xs sm:text-sm text-indigo-300/50 ml-1" data-id="element-23979">
                    / мес
                  </span>
                </div>
              </div>

              <div className="h-px bg-indigo-500/20 mb-5" data-id="element-23980"></div>

              <ul className="space-y-3 mb-8 flex-grow text-sm" data-id="element-23981">
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-23982">
                  <div className="w-5 h-5 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0" data-id="element-23983">
                    <Calendar className="w-3 h-3 text-indigo-400" data-id="element-23984" />
                  </div>
                  <span data-id="element-23985">
                    <span className="text-indigo-300 font-semibold" data-id="element-23986">
                      Безлимит
                    </span>{' '}
                    консультаций
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-23987">
                  <div className="w-5 h-5 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0" data-id="element-23988">
                    <Wallet className="w-3 h-3 text-indigo-400" data-id="element-23989" />
                  </div>
                  <span data-id="element-23990">
                    Комиссия{' '}
                    <span className="text-indigo-300 font-semibold" data-id="element-23991">15%</span>{' '}
                    <span className="text-emerald-400 text-xs" data-id="element-23992">(-10%)</span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-23993">
                  <div className="w-5 h-5 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0" data-id="element-23994">
                    <Search className="w-3 h-3 text-indigo-400" data-id="element-23995" />
                  </div>
                  <span data-id="element-23996">Приоритет в каталоге</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-23997">
                  <div className="w-5 h-5 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0" data-id="element-23998">
                    <Briefcase className="w-3 h-3 text-indigo-400" data-id="element-23999" />
                  </div>
                  <span data-id="element-24000">
                    До <span className="text-indigo-300 font-semibold" data-id="element-24001">20</span>{' '}
                    услуг
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24002">
                  <div className="w-5 h-5 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24003">
                    <BarChart3 className="w-3 h-3 text-indigo-400" data-id="element-24004" />
                  </div>
                  <span data-id="element-24005">Полная аналитика</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24006">
                  <div className="w-5 h-5 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24007">
                    <MessageSquare className="w-3 h-3 text-indigo-400" data-id="element-24008" />
                  </div>
                  <span data-id="element-24009">Приоритетная поддержка</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24010">
                  <div className="w-5 h-5 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24011">
                    <TrendingUp className="w-3 h-3 text-indigo-400" data-id="element-24012" />
                  </div>
                  <span data-id="element-24013">Базовое продвижение</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24014">
                  <div className="w-5 h-5 rounded-md bg-emerald-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24015">
                    <Check className="w-3 h-3 text-emerald-400" data-id="element-24016" />
                  </div>
                  <span data-id="element-24017">
                    Скидка{' '}
                    <span className="text-emerald-400 font-semibold" data-id="element-24018">10%</span>{' '}
                    на пакеты
                  </span>
                </li>
              </ul>
              <Button fullWidth className="!bg-indigo-600 hover:!bg-indigo-500 !text-white border-none shadow-[0_0_20px_rgba(99,102,241,0.35)]" onClick={scrollToForm} data-id="element-24019">
                Выбрать PRO
              </Button>
            </div>

            {/* TOP */}
            <div className="bg-gradient-to-b from-amber-950/30 to-[#050b14] rounded-2xl border border-amber-500/20 p-6 flex flex-col hover:border-amber-500/40 transition-all group" data-id="element-24020">
              <div className="mb-5" data-id="element-24021">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center mb-4" data-id="element-24022">
                  <Sparkles className="w-5 h-5 text-amber-400" data-id="element-24023" />
                </div>
                <h3 className="text-lg font-bold text-amber-300" data-id="element-24024">TOP</h3>
                <div className="mt-2" data-id="element-24025">
                  <span className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-24026">
                    2 490 ₽
                  </span>
                  <span className="text-xs sm:text-sm text-amber-300/50 ml-1" data-id="element-24027">
                    / мес
                  </span>
                </div>
              </div>

              <div className="h-px bg-amber-500/15 mb-5" data-id="element-24028"></div>

              <ul className="space-y-3 mb-8 flex-grow text-sm" data-id="element-24029">
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24030">
                  <div className="w-5 h-5 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24031">
                    <Calendar className="w-3 h-3 text-amber-400" data-id="element-24032" />
                  </div>
                  <span data-id="element-24033">
                    <span className="text-amber-300 font-semibold" data-id="element-24034">
                      Безлимит
                    </span>{' '}
                    консультаций
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24035">
                  <div className="w-5 h-5 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24036">
                    <Wallet className="w-3 h-3 text-amber-400" data-id="element-24037" />
                  </div>
                  <span data-id="element-24038">
                    Комиссия{' '}
                    <span className="text-amber-300 font-semibold" data-id="element-24039">10%</span>{' '}
                    <span className="text-emerald-400 text-xs" data-id="element-24040">(-15%)</span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24041">
                  <div className="w-5 h-5 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24042">
                    <Search className="w-3 h-3 text-amber-400" data-id="element-24043" />
                  </div>
                  <span data-id="element-24044">
                    <span className="text-amber-300 font-semibold" data-id="element-24045">Топ</span> в
                    поиске
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24046">
                  <div className="w-5 h-5 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24047">
                    <Briefcase className="w-3 h-3 text-amber-400" data-id="element-24048" />
                  </div>
                  <span data-id="element-24049">
                    <span className="text-amber-300 font-semibold" data-id="element-24050">
                      Безлимит
                    </span>{' '}
                    услуг
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24051">
                  <div className="w-5 h-5 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24052">
                    <BarChart3 className="w-3 h-3 text-amber-400" data-id="element-24053" />
                  </div>
                  <span data-id="element-24054">
                    Полная аналитика +{' '}
                    <span className="text-amber-300 font-semibold" data-id="element-24055">AI</span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24056">
                  <div className="w-5 h-5 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24057">
                    <Users className="w-3 h-3 text-amber-400" data-id="element-24058" />
                  </div>
                  <span data-id="element-24059">Персональный менеджер</span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24060">
                  <div className="w-5 h-5 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24061">
                    <TrendingUp className="w-3 h-3 text-amber-400" data-id="element-24062" />
                  </div>
                  <span data-id="element-24063">
                    <span className="text-amber-300 font-semibold" data-id="element-24064">
                      Премиум
                    </span>{' '}
                    продвижение
                  </span>
                </li>
                <li className="flex items-center gap-2.5 text-slate-200" data-id="element-24065">
                  <div className="w-5 h-5 rounded-md bg-emerald-500/15 flex items-center justify-center flex-shrink-0" data-id="element-24066">
                    <Check className="w-3 h-3 text-emerald-400" data-id="element-24067" />
                  </div>
                  <span data-id="element-24068">
                    Скидка{' '}
                    <span className="text-emerald-400 font-semibold" data-id="element-24069">20%</span>{' '}
                    на пакеты
                  </span>
                </li>
              </ul>
              <Button fullWidth className="!bg-gradient-to-r !from-amber-600 !to-orange-600 hover:!from-amber-500 hover:!to-orange-500 !text-white border-none shadow-[0_0_20px_rgba(245,158,11,0.25)]" onClick={scrollToForm} data-id="element-24070">
                Выбрать TOP
              </Button>
            </div>
          </div>

          <p className="text-center text-xs text-slate-500 mt-8" data-id="element-24071">
            Все тарифы включают базовый профиль, чат с клиентами и систему
            отзывов. Отмена в любой момент.
          </p>
        </div>
      </section>

      {/* 9. ОТЗЫВЫ */}
      <section className="py-12 sm:py-16 relative" data-id="element-24072">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-24073">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-8 text-center" data-id="element-24074">
            Что говорят астрологи
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-id="element-24075">
            {/* Featured review — large */}
            <div className="md:col-span-7 bg-gradient-to-br from-indigo-950/60 to-[#0a1628] p-6 rounded-2xl border border-indigo-500/15 relative" data-id="element-24076">
              <div className="flex items-center gap-3 mb-4" data-id="element-24077">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-sm font-bold text-white shadow-lg" data-id="element-24078">
                  Е
                </div>
                <div data-id="element-24079">
                  <div className="text-sm font-bold text-white" data-id="element-24080">
                    Елена Соколова
                  </div>
                  <div className="text-xs text-indigo-400" data-id="element-24081">
                    Ведический астролог · 3 года
                  </div>
                </div>
                <div className="ml-auto flex gap-0.5" data-id="element-24082">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-24083" />)}
                </div>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed mb-4" data-id="element-24084">
                «Получаю стабильные заявки каждую неделю. Очень удобно, что не
                нужно самостоятельно искать клиентов — платформа делает это за
                меня. За 3 месяца доход вырос на 60%.»
              </p>
              <div className="flex items-center gap-4 text-xs" data-id="element-24085">
                <span className="text-emerald-400 font-semibold bg-emerald-500/10 px-2.5 py-1 rounded-full" data-id="element-24086">
                  +60% доход
                </span>
                <span className="text-slate-500" data-id="element-24087">На платформе 8 месяцев</span>
              </div>
            </div>

            {/* Two smaller reviews stacked */}
            <div className="md:col-span-5 flex flex-col gap-4" data-id="element-24088">
              <div className="bg-[#0a1628] p-5 rounded-2xl border border-white/5 flex-1" data-id="element-24089">
                <div className="flex items-center gap-3 mb-3" data-id="element-24090">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-xs font-bold text-white" data-id="element-24091">
                    М
                  </div>
                  <div data-id="element-24092">
                    <div className="text-sm font-bold text-white" data-id="element-24093">
                      Михаил В.
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-24094">
                      Западная астрология · 5 лет
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed" data-id="element-24095">
                  «Генерация отчётов экономит 3-4 часа в день. Всё в одном месте
                  — от заявок до выплат.»
                </p>
              </div>

              <div className="bg-[#0a1628] p-5 rounded-2xl border border-white/5 flex-1" data-id="element-24096">
                <div className="flex items-center gap-3 mb-3" data-id="element-24097">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-xs font-bold text-white" data-id="element-24098">
                    А
                  </div>
                  <div data-id="element-24099">
                    <div className="text-sm font-bold text-white" data-id="element-24100">Анна К.</div>
                    <div className="text-[10px] text-slate-500" data-id="element-24101">
                      Начинающий · 1 год
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed" data-id="element-24102">
                  «Отличный инструмент для старта. Первые клиенты пришли через 2
                  дня после модерации.»
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-12 sm:py-16 bg-[#0a1628] border-y border-white/5" data-id="element-24103">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-24104">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start" data-id="element-24105">
            {/* Left: heading + trust */}
            <div className="lg:col-span-2 lg:sticky lg:top-24" data-id="element-24106">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-24107">
                Частые вопросы
              </h2>
              <p className="text-sm text-slate-400 mb-6" data-id="element-24108">
                Не нашли ответ? Напишите нам — ответим за 15 минут.
              </p>
              <div className="hidden lg:block space-y-3" data-id="element-24109">
                <div className="flex items-center gap-3 bg-[#050b14] rounded-xl p-3 border border-white/5" data-id="element-24110">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0" data-id="element-24111">
                    <Check className="w-4 h-4 text-emerald-400" data-id="element-24112" />
                  </div>
                  <div data-id="element-24113">
                    <div className="text-xs font-semibold text-white" data-id="element-24114">
                      500+ астрологов
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-24115">
                      уже работают на платформе
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#050b14] rounded-xl p-3 border border-white/5" data-id="element-24116">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0" data-id="element-24117">
                    <Star className="w-4 h-4 text-amber-400" data-id="element-24118" />
                  </div>
                  <div data-id="element-24119">
                    <div className="text-xs font-semibold text-white" data-id="element-24120">
                      4.8 из 5
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-24121">
                      средняя оценка платформы
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#050b14] rounded-xl p-3 border border-white/5" data-id="element-24122">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0" data-id="element-24123">
                    <MessageSquare className="w-4 h-4 text-indigo-400" data-id="element-24124" />
                  </div>
                  <div data-id="element-24125">
                    <div className="text-xs font-semibold text-white" data-id="element-24126">
                      Поддержка 24/7
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-24127">
                      чат, email, телеграм
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: accordion */}
            <div className="lg:col-span-3" data-id="element-24128">
              <Accordion items={faqItems} className="[&>div]:bg-[#050b14] [&>div]:border-white/5 [&>div]:rounded-xl [&>div]:mb-2 [&_button]:text-white [&_button_span]:text-slate-200 [&_button_span]:font-medium [&_button_span]:text-sm [&_svg]:text-indigo-400 [&_.text-slate-600]:text-slate-400 [&_.text-slate-600]:text-sm" data-id="element-24129" />
            </div>
          </div>
        </div>
      </section>

      {/* 11. ФИНАЛЬНЫЙ БЛОК */}
      <section ref={formRef} className="py-14 sm:py-20 relative overflow-hidden" data-id="element-24130">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900/15 via-[#050b14] to-[#050b14]" data-id="element-24131"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-24132">
          {!formSubmitted ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-24133">
              {/* Left: Benefits */}
              <div className="flex flex-col justify-center" data-id="element-24134">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-24135">
                  Начните зарабатывать на&nbsp;астрологии
                </h2>
                <p className="text-sm text-slate-400 mb-6" data-id="element-24136">
                  Заполните форму — мы проверим профиль и откроем доступ за 24
                  часа
                </p>

                <div className="space-y-3 mb-6" data-id="element-24137">
                  {[{
                icon: <Wallet className="w-4 h-4 text-emerald-400" data-id="element-24138" />,
                text: 'Бесплатный старт',
                sub: 'Базовый тариф 0 ₽/мес'
              }, {
                icon: <Users className="w-4 h-4 text-indigo-400" data-id="element-24139" />,
                text: 'Готовый поток клиентов',
                sub: 'Не нужно искать самостоятельно'
              }, {
                icon: <FileText className="w-4 h-4 text-amber-400" data-id="element-24140" />,
                text: 'AI-генерация отчётов',
                sub: 'Экономия 3-4 часов в день'
              }].map((b, i) => <div key={i} className="flex items-center gap-3" data-id="element-24141">
                      <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center flex-shrink-0" data-id="element-24142">
                        {b.icon}
                      </div>
                      <div data-id="element-24143">
                        <div className="text-sm font-semibold text-white" data-id="element-24144">
                          {b.text}
                        </div>
                        <div className="text-[11px] text-slate-500" data-id="element-24145">
                          {b.sub}
                        </div>
                      </div>
                    </div>)}
                </div>

                {/* Social proof mini */}
                <div className="hidden lg:flex items-center gap-2 bg-white/[0.03] rounded-xl px-4 py-3 border border-white/5" data-id="element-24146">
                  <div className="flex -space-x-2" data-id="element-24147">
                    {['Е', 'М', 'А', 'И'].map((l, i) => <div key={i} className={`w-7 h-7 rounded-full border-2 border-[#050b14] flex items-center justify-center text-[9px] font-bold text-white ${['bg-violet-600', 'bg-indigo-600', 'bg-amber-600', 'bg-emerald-600'][i]}`} data-id="element-24148">
                        {l}
                      </div>)}
                  </div>
                  <div className="text-[11px] text-slate-400" data-id="element-24149">
                    <span className="text-white font-semibold" data-id="element-24150">500+</span>{' '}
                    астрологов уже на платформе
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-[#0a1628]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-2xl" data-id="element-24151">
                <h3 className="text-base font-bold text-white mb-5 text-center" data-id="element-24152">
                  Оставить заявку
                </h3>
                <form onSubmit={handleRegister} className="space-y-3.5" data-id="element-24153">
                  <Input placeholder="Ваше имя" required className="[&_input]:bg-[#050b14] [&_input]:border-white/10 [&_input]:text-white [&_input]:placeholder-slate-500 [&_input]:h-11 [&_input]:rounded-xl [&_input]:text-sm focus-within:[&_input]:border-indigo-500/50 focus-within:[&_input]:ring-1 focus-within:[&_input]:ring-indigo-500/50" data-id="element-24154" />
                  <Input type="email" placeholder="Email" required className="[&_input]:bg-[#050b14] [&_input]:border-white/10 [&_input]:text-white [&_input]:placeholder-slate-500 [&_input]:h-11 [&_input]:rounded-xl [&_input]:text-sm focus-within:[&_input]:border-indigo-500/50 focus-within:[&_input]:ring-1 focus-within:[&_input]:ring-indigo-500/50" data-id="element-24155" />
                  <Input placeholder="Ссылка на соцсети или сайт" className="[&_input]:bg-[#050b14] [&_input]:border-white/10 [&_input]:text-white [&_input]:placeholder-slate-500 [&_input]:h-11 [&_input]:rounded-xl [&_input]:text-sm focus-within:[&_input]:border-indigo-500/50 focus-within:[&_input]:ring-1 focus-within:[&_input]:ring-indigo-500/50" data-id="element-24156" />
                  <select defaultValue="" className="w-full h-11 px-4 rounded-xl border border-white/10 bg-[#050b14] text-slate-300 text-sm focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 appearance-none cursor-pointer" data-id="element-24157">
                    <option value="" disabled data-id="element-24158">
                      Ваш опыт работы
                    </option>
                    <option value="1" data-id="element-24159">До 1 года</option>
                    <option value="3" data-id="element-24160">1-3 года</option>
                    <option value="5" data-id="element-24161">Более 3 лет</option>
                  </select>

                  <label className="flex items-start gap-2.5 cursor-pointer group mt-4" data-id="element-24162">
                    <input type="checkbox" required className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500/50" data-id="element-24163" />
                    <span className="text-[11px] text-slate-500 leading-relaxed group-hover:text-slate-400 transition-colors" data-id="element-24164">
                      Я соглашаюсь с{' '}
                      <button type="button" onClick={() => onNavigate('public-offer')} className="text-indigo-400 hover:underline" data-id="element-24165">
                        публичной офертой
                      </button>{' '}
                      и{' '}
                      <button type="button" onClick={() => onNavigate('privacy')} className="text-indigo-400 hover:underline" data-id="element-24166">
                        политикой конфиденциальности
                      </button>
                    </span>
                  </label>

                  <Button type="submit" fullWidth className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 !text-slate-900 font-bold shadow-[0_0_20px_rgba(245,158,11,0.25)] border-none !mt-4" data-id="element-24167">
                    Отправить заявку
                  </Button>
                  <p className="text-center text-[11px] text-slate-500 mt-3" data-id="element-24168">
                    Модерация до 24 часов · Бесплатно
                  </p>
                </form>
              </div>
            </div> : <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-16" data-id="element-24169">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-5 shadow-[0_0_30px_rgba(16,185,129,0.2)]" data-id="element-24170">
                <Check className="w-8 h-8 text-emerald-400" data-id="element-24171" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-24172">
                Заявка принята!
              </h3>
              <p className="text-sm text-slate-400" data-id="element-24173">
                Перенаправляем в личный кабинет...
              </p>
            </motion.div>}
        </div>
      </section>
    </div>;
}